import { ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import { takeUntil } from 'rxjs';
import {
  IFacetedFilterDataItem,
  IReplayItem,
  ISelectionInfo,
  ISelectedHierarchyInfo,
  ISelectedFacetInfo,
  IHierarchyValue,
} from '../types/faceted-filter.types';
import { PaneComponentFactory } from '../Utils/pane-component.factory';
import { PaneInstanceManager } from '../Utils/pane-instance.manager';
import { TypeCheck } from '../Utils/type-check';
import { FilterPanelValueService } from './filter-panel-value.service';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IPaneInstance = any;

@Injectable()
export class FilterPaneManagerService {
  private _host!: ViewContainerRef;

  private _noPathReplayItemStash: IReplayItem | undefined;

  componentFactory!: PaneComponentFactory;

  constructor(private valueService: FilterPanelValueService) {}

  setContainerRefHost(host: ViewContainerRef): void {
    this._host = host;
    this.componentFactory = new PaneComponentFactory(host);
  }

  hasContainerRefHost(): boolean {
    return !!this._host;
  }

  loadFilterPane(item: IFacetedFilterDataItem): void {
    PaneInstanceManager.removeChildItemsIfExists(item.path);
    const componentInstance = this.componentFactory.loadComponentInstance(item);
    PaneInstanceManager.addActiveInstance(componentInstance);
    this.setNonReplayableItemValue(componentInstance);
    this.watchPaneInstanceEvents(componentInstance);
  }

  loadReplayFilterPane(replayItem: IReplayItem): void {
    PaneInstanceManager.removeChildItemsIfExists(replayItem.item.path);
    const componentInstance = this.componentFactory.loadReplayComponentInstance(replayItem);
    PaneInstanceManager.addActiveInstance(componentInstance);
    this.watchPaneInstanceEvents(componentInstance);
  }

  replayResult(replayItems: IReplayItem[]): void {
    this.stashNonReplayableItemIfExists(replayItems);

    for (let i = 0; i < replayItems.length; i++) {
      const replayItem = replayItems[i];
      if (replayItem && TypeCheck.isReplayableContainer(replayItem.item.type)) {
        this.loadReplayFilterPane(replayItem);
      }
    }
  }

  purgeAllLoadedComponents(): void {
    PaneInstanceManager.purgeActiveInstances();
    this.valueService.clearSelectionInfo();
  }
  // TODO: Find the overlap below and consolidate
  private watchPaneInstanceEvents(componentInstance: ComponentRef<IPaneInstance>): void {
    if (componentInstance.instance.onContainerItemSelected) {
      componentInstance.instance.onContainerItemSelected
        .pipe(takeUntil(componentInstance.instance.destroy$))
        .subscribe((item: IFacetedFilterDataItem) => {
          this.onContainerItemSelected(item);
        });
    }

    if (componentInstance.instance.onCheckableItemsChanged) {
      componentInstance.instance.onCheckableItemsChanged
        .pipe(takeUntil(componentInstance.instance.destroy$))
        .subscribe(($event: ISelectionInfo) => {
          this.onCheckableItemsChanged($event);
        });
    }

    if (componentInstance.instance.onOperatorExpressionSelected) {
      componentInstance.instance.onOperatorExpressionSelected
        .pipe(takeUntil(componentInstance.instance.destroy$))
        .subscribe(($event: ISelectionInfo) => {
          this.onOperatorExpressionSelected($event);
        });
    }

    if (componentInstance.instance.onDateRangeSelected) {
      componentInstance.instance.onDateRangeSelected
        .pipe(takeUntil(componentInstance.instance.destroy$))
        .subscribe(($event: ISelectionInfo) => {
          this.onDateRangeSelected($event);
        });
    }

    if (componentInstance.instance.onHierarchyContainerItemsChanged) {
      componentInstance.instance.onHierarchyContainerItemsChanged
        .pipe(takeUntil(componentInstance.instance.destroy$))
        .subscribe((item: ISelectedHierarchyInfo) => {
          this.onHierarchyContainerItemsChanged(item);
        });
    }

    if (componentInstance.instance.onHierarchyItemsChanged) {
      componentInstance.instance.onHierarchyItemsChanged
        .pipe(takeUntil(componentInstance.instance.destroy$))
        .subscribe((item: ISelectedHierarchyInfo) => {
          this.onHierarchyItemsChanged(item);
        });
    }

    if (componentInstance.instance.onHierarchyReplaySelection) {
      componentInstance.instance.onHierarchyReplaySelection
        .pipe(takeUntil(componentInstance.instance.destroy$))
        .subscribe((item: ISelectedHierarchyInfo) => {
          this.onHierarchyReplaySelection(item);
        });
    }

    setTimeout(() => {
      componentInstance.location.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 1);
  }

  private onContainerItemSelected(item: IFacetedFilterDataItem): void {
    if (TypeCheck.isFacet(item.type)) {
      PaneInstanceManager.removeChildItemsIfExists(item.path);
      // TODO: Add Items to Value Store if ever needed...We'll see
    } else {
      this.valueService.clearSelectionInfo();
      this.loadFilterPane(item);
    }
  }

  private onCheckableItemsChanged(selectionInfo: ISelectionInfo): void {
    if (TypeCheck.isFacet(selectionInfo.item.type))
      PaneInstanceManager.removeChildItemsIfExists(selectionInfo.item.path);

    const selection = <ISelectedFacetInfo>selectionInfo;
    if (selection.facets && selection.facets.length) this.valueService.setSelectionInfo(selection);
    else {
      this.valueService.clearSelectionInfo();
    }
  }

  private onOperatorExpressionSelected(selectionInfo: ISelectionInfo): void {
    this.valueService.setSelectionInfo(selectionInfo);
  }

  private onDateRangeSelected(selectionInfo: ISelectionInfo): void {
    this.valueService.setSelectionInfo(selectionInfo);
  }

  private onHierarchyContainerItemsChanged(selectionInfo: ISelectedHierarchyInfo): void {
    this.valueService.clearSelectionInfo();
    this.valueService.setSelectionInfo(selectionInfo);

    if (!selectionInfo.hierarchies.length) {
      this.valueService.clearSelectionInfo();
    }
    if (selectionInfo.purgePath) PaneInstanceManager.removeChildItemsIfExists(selectionInfo.purgePath);
    else this.loadFilterPane(selectionInfo.item);
  }

  private onHierarchyItemsChanged(selectionInfo: ISelectedHierarchyInfo): void {
    if (selectionInfo.hierarchies && selectionInfo.hierarchies.length)
      this.valueService.setSelectionInfo(selectionInfo);
    else {
      this.valueService.clearSelectionInfo();
      const parentSelection: ISelectedHierarchyInfo = {
        hierarchies: [<IHierarchyValue>selectionInfo.item.value],
        item: selectionInfo.item,
      };
      this.valueService.setSelectionInfo(parentSelection);
    }
  }

  private onHierarchyReplaySelection(selectionInfo: ISelectedHierarchyInfo): void {
    this.valueService.clearSelectionInfo();
    this.valueService.setSelectionInfo(selectionInfo);
  }

  private stashNonReplayableItemIfExists(replayItems: IReplayItem[]): void {
    this._noPathReplayItemStash = replayItems.find((ri) => !TypeCheck.isReplayableContainer(ri.item.type)) ?? undefined;
  }

  private setNonReplayableItemValue(component: ComponentRef<IPaneInstance>): void {
    if (this._noPathReplayItemStash) {
      component.instance.replayValue = this._noPathReplayItemStash.value;
      this._noPathReplayItemStash = undefined;
    }
  }
}
